import { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Ad, AdPositionEnum } from "../client/api";
import { AdPositionEnum as AdPos } from "../client/api";
import { isAdActive, pickAd } from "../config/utils";
import { buildAdsKey, fetchAdsByPosition } from "../store/ads/adsSlice";
import type { AppDispatch, RootState } from "../store";
import { ClavaContext } from "../config/contexts";
import { AdSensePositionEnum } from "../config/adsenseConfig";

type UseAdSelectionOptions = {
  type: AdPositionEnum | AdSensePositionEnum;
  canShowAdSense?: boolean;
  skipSelection?: boolean;
};

export function useAdSelection({
                                 type,
                                 canShowAdSense = false,
                                 skipSelection = false
                               }: UseAdSelectionOptions) {
  const dispatch = useDispatch<AppDispatch>();
  const adsState = useSelector((state: RootState) => state.ads.value);
  const { pro, aoi } = useContext(ClavaContext);


  const filteredAds = useMemo(() => {
    if (!type) {
      return undefined;
    }


    const allAds: Ad[] = [];
    let anyUndefined = false;
    const positions: (AdPositionEnum | AdSensePositionEnum)[] =
      type === AdPos.HOME_MATCH
        ? [AdPos.HOME_MATCH, AdPos.HOME_MATCH_DOUBLE]
        : [type];
    positions.forEach((pos) => {
      const key = buildAdsKey(pos, aoi);
      const ads = adsState[key];
      if (ads === undefined) {
        anyUndefined = true;
      } else {
        allAds.push(...ads);
      }
    });

    if (anyUndefined && allAds.length === 0) {
      return undefined;
    }

    return allAds.filter(isAdActive);
  }, [adsState, aoi, type]);

  useEffect(() => {
    if (!type || pro) {
      return;
    }

    if (filteredAds !== undefined) {
      return;
    }
    const positions: (AdPositionEnum | AdSensePositionEnum)[] =
      type === AdPos.HOME_MATCH
        ? [AdPos.HOME_MATCH, AdPos.HOME_MATCH_DOUBLE]
        : [type];
    positions.forEach((pos) => {
      const key = buildAdsKey(pos, aoi);
      if (adsState[key] === undefined) {
        if (pos === AdSensePositionEnum.MATCH_RIGHT_RAIL || pos === AdSensePositionEnum.PLAYER_RIGHT_RAIL || pos === AdSensePositionEnum.TEAM_RIGHT_RAIL)
          return;
        dispatch(fetchAdsByPosition(pos, aoi));
      }
    });
  }, [dispatch, aoi, type, filteredAds, adsState, pro]);

  const selectedAd = useMemo(() => {
    if (skipSelection || !filteredAds || filteredAds.length === 0) {
      return null;
    }

    return pickAd(filteredAds, canShowAdSense);
  }, [filteredAds, canShowAdSense, skipSelection]);

  return {
    selectedAd,
    filteredAds,
    pro
  };
}

